import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientService } from '../services/api';
import { Client } from '../types';
import Card from '../components/ui/Card';
import StatsDashboard from '../components/StatsDashboard';
import Button from '../components/ui/Button';
import IPRestrictions from '../components/IPRestrictions';

const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRegeneratingKey, setIsRegeneratingKey] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const checkAgentStatus = async (clientId: string) => {
    try {
      const statusResponse = await clientService.getClientStatus(clientId);
      setClient(prev => prev ? { ...prev, status: statusResponse.status } : null);
    } catch (err) {
      // Ignorer les erreurs de statut pour éviter de perturber l'interface
    }
  };

  const fetchClient = useCallback(async () => {
    if (!id) return;
    
    try {
      setIsLoading(true);
      const data = await clientService.getClientById(id);
      setClient(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des détails du client');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  useEffect(() => {
    // Mettre en place un intervalle pour rafraîchir le statut de l'agent
    const interval = setInterval(() => {
      if (client?.id) {
        checkAgentStatus(client.id);
      }
    }, 10000); // Rafraîchir toutes les 10 secondes

    return () => {
      clearInterval(interval);
    };
  }, [client?.id]);

  const handleRegenerateApiKey = async () => {
    if (!client?.id) return;
    
    try {
      setIsRegeneratingKey(true);
      const response = await clientService.regenerateApiKey(client.id);
      setClient(prev => prev ? { ...prev, apiKey: response.apiKey } : null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la régénération de la clé API');
    } finally {
      setIsRegeneratingKey(false);
    }
  };

  const handleDownloadAgent = async () => {
    if (!client?.id) return;
    
    try {
      setIsDownloading(true);
      
      // Récupérer le token d'authentification
      const token = localStorage.getItem('token');
      
      // Créer une URL avec le token dans les paramètres de requête
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const downloadUrl = `${baseUrl}/clients/${client.id}/download?token=${token}`;
      
      // Ouvrir l'URL dans un nouvel onglet pour déclencher le téléchargement
      window.open(downloadUrl, '_blank');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors du téléchargement de la configuration');
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <Card>
            <div className="text-center py-6">
              <svg className="mx-auto h-12 w-12 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">Erreur</h3>
              <p className="mt-1 text-sm text-gray-500">{error || 'Client non trouvé'}</p>
              <div className="mt-6">
                <Button variant="primary" onClick={() => navigate('/dashboard')}>
                  Retour au tableau de bord
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Détails du client</h1>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Retour
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card title="Informations générales">
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Nom</h4>
                <p className="mt-1 text-lg font-medium text-gray-900">{client.name}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Sous-domaine</h4>
                <p className="mt-1 text-lg font-medium text-blue-600">{client.subdomain}.api100.io</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Statut du tunnel</h4>
                <div className="mt-1 flex items-center">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      client.status === 'online'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {client.status === 'online' ? 'En ligne' : 'Hors ligne'}
                  </span>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Date de création</h4>
                <p className="mt-1 text-sm text-gray-900">
                  {client.createdAt ? new Date(client.createdAt).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'Non disponible'}
                </p>
              </div>
            </div>
          </Card>

          <Card title="Sécurité et accès">
            <div className="space-y-4">
              {client.apiKey && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Clé API</h4>
                  <div className="mt-1 flex items-center">
                    <code className="text-sm bg-gray-100 p-2 rounded flex-1 overflow-x-auto">
                      {client.apiKey}
                    </code>
                  </div>
                </div>
              )}
              
              <div className="pt-4">
                <Button
                  variant="danger"
                  onClick={handleRegenerateApiKey}
                  isLoading={isRegeneratingKey}
                >
                  Régénérer la clé API
                </Button>
                <p className="mt-2 text-xs text-gray-500">
                  Attention : cette action invalidera l'ancienne clé et nécessitera une mise à jour de la configuration de l'agent.
                </p>
              </div>
            </div>
          </Card>
          
          {/* Restrictions d'adresses IP */}
          {client.id && <IPRestrictions clientId={client.id} />}

          <Card title="Agent local">
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                L'agent local permet d'établir un tunnel sécurisé entre votre serveur Sage 100 et notre plateforme API.
                Téléchargez l'agent préconfiguré pour ce client et installez-le sur le serveur où Sage 100 est installé.
              </p>
              
              <div className="pt-2">
                <Button 
                  variant="primary" 
                  onClick={handleDownloadAgent}
                  isLoading={isDownloading}
                >
                  Télécharger la configuration
                </Button>
              </div>
              
              
              <div className="pt-4">
                <h4 className="text-sm font-medium text-gray-500">Instructions d'installation .NET</h4>
                <ol className="mt-2 text-sm text-gray-600 list-decimal pl-5 space-y-2">
                  <li>Téléchargez la configuration</li>
                  <li>Téléchargez et installez l'agent .NET depuis <a href="https://github.com/api100-io/agent-dotnet/releases" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">GitHub</a></li>
                  <li>Placez le fichier de configuration dans <code>%APPDATA%\API100\agent-config.json</code></li>
                  <li>Lancez l'agent et spécifiez le port local lorsque demandé</li>
                </ol>
              </div>
            </div>
          </Card>

          <Card title="Statut et diagnostics">
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">État actuel</h4>
                <div className="mt-2">
                  <div className="flex items-center">
                    <div
                      className={`h-4 w-4 rounded-full mr-2 ${
                        client.status === 'online' ? 'bg-green-500' : 'bg-red-500'
                      }`}
                    ></div>
                    <span className="text-sm font-medium">
                      {client.status === 'online' ? 'Tunnel actif' : 'Tunnel inactif'}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {client.status === 'online'
                      ? 'Le tunnel est correctement établi et les requêtes API peuvent être traitées.'
                      : 'Le tunnel n\'est pas actif. Vérifiez que l\'agent est bien installé et en cours d\'exécution.'}
                  </p>
                </div>
              </div>
              
              <div className="pt-4">
                <Button variant="outline">
                  Vérifier la connectivité
                </Button>
              </div>
            </div>
          </Card>
          
          {/* Statistiques du client */}
          <div className="mt-6">
            <Card>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Statistiques du client</h2>
              <StatsDashboard clientId={client.id} />
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;
